import React, { useState } from 'react';
import { Table, Input, Select, Tag, Typography, Space, DatePicker, Collapse } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { ColumnsType } from 'antd/es/table';
import { API } from './services/api';
import { useIsMobile } from './hooks/useIsMobile';
import dayjs, { Dayjs } from 'dayjs';

const { Text } = Typography;

const BEAT_COVERAGE = [
  {
    beat: '서울 북부',
    color: 'blue',
    items: [
      { label: '기관', tags: ['경찰청', '서울경찰청', '서울시경', '북부지검', '북부지법', '인권위'] },
      { label: '혜화·도봉라인', tags: ['혜화서', '동대문서', '중랑서', '도봉서', '강북서', '노원서'] },
    ],
  },
  {
    beat: '서울 서부/중부',
    color: 'green',
    items: [
      { label: '기관', tags: ['서부지검', '서부지법'] },
      { label: '종로라인', tags: ['종로서', '성북서', '종암서'] },
      { label: '마포라인', tags: ['마포서', '서대문서', '은평서', '서부서', '강수대', '마수대', '금수대', '반부패수사대'] },
      { label: '중부라인', tags: ['중부서', '용산서', '남대문서'] },
    ],
  },
  {
    beat: '서울 남부',
    color: 'orange',
    items: [
      { label: '기관', tags: ['남부지검', '증권범죄합수단', '남부지법'] },
      { label: '영등포라인', tags: ['영등포서', '구로서', '강서서', '양천서'] },
      { label: '관악라인', tags: ['관악서', '금천서', '동작서', '방배서'] },
    ],
  },
  {
    beat: '서울 동부/강남',
    color: 'purple',
    items: [
      { label: '기관', tags: ['동부지검', '동부지법'] },
      { label: '강남라인', tags: ['강남서', '서초서', '수서서', '강동서', '송파서'] },
      { label: '광진라인', tags: ['광진서', '성동서'] },
    ],
  },
];

interface SocialArticle {
  _id: string;
  title: string;
  link: string;
  summary: string;
  author: string;
  publishedAt: string;
  source: string;
  imageUrl: string;
  crawledAt: string;
  beat: string[];
  matchedKeywords: string[];
}

const BEAT_OPTIONS = [
  { label: '전체 라인', value: '' },
  { label: '서울 북부', value: '서울 북부' },
  { label: '서울 서부/중부', value: '서울 서부/중부' },
  { label: '서울 남부', value: '서울 남부' },
  { label: '서울 동부/강남', value: '서울 동부/강남' },
];

const BEAT_COLOR: Record<string, string> = {
  '서울 북부': 'blue',
  '서울 서부/중부': 'green',
  '서울 남부': 'orange',
  '서울 동부/강남': 'purple',
};

const SOURCE_LABEL: Record<string, string> = {
  'naver-social': '네이버 사회',
  'naver-social-249': '네이버 249',
  'naver-social-251': '네이버 251',
  'naver-social-59b': '네이버 59b',
  'naver-social-257': '네이버 257',
  'daum-social': '다음 사회',
  'daum-welfare': '다음 복지',
  'daum-labor': '다음 노동',
  'daum-safety': '다음 안전',
  'daum-affair': '다음 사건',
  'daum-law': '다음 법원',
};

const fetchSocialArticles = async (params: {
  keyword: string;
  beat: string;
  matchedKeyword: string;
  startDate: string;
  endDate: string;
  page: number;
  limit: number;
}) => {
  const query = new URLSearchParams();
  if (params.keyword) query.set('keyword', params.keyword);
  if (params.beat) query.set('beat', params.beat);
  if (params.matchedKeyword) query.set('matchedKeyword', params.matchedKeyword);
  if (params.startDate) query.set('startDate', params.startDate);
  if (params.endDate) query.set('endDate', params.endDate);
  query.set('page', String(params.page));
  query.set('limit', String(params.limit));

  const res = await API.get(`/social-articles/search?${query.toString()}`);
  return res.data as { total: number; contents: SocialArticle[] };
};

const SocialNews: React.FC = () => {
  const isMobile = useIsMobile();
  const [keyword, setKeyword] = useState('');
  const [beat, setBeat] = useState('');
  const [matchedKeyword, setMatchedKeyword] = useState('');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [page, setPage] = useState(1);
  const limit = 20;

  const startDate = selectedDate?.startOf('day').toISOString() ?? '';
  const endDate = selectedDate?.endOf('day').toISOString() ?? '';

  const { data, isLoading } = useQuery({
    queryKey: ['socialNews', { keyword, beat, matchedKeyword, startDate, endDate, page, limit }],
    queryFn: () => fetchSocialArticles({ keyword, beat, matchedKeyword, startDate, endDate, page, limit }),
    refetchOnWindowFocus: false,
  });

  const DOW = ['일', '월', '화', '수', '목', '금', '토'];

  // 모바일: 날짜 컬럼 숨기고 기사 셀에 날짜 포함
  const desktopDateColumn: ColumnsType<SocialArticle>[0] = {
    title: '날짜',
    dataIndex: 'crawledAt',
    key: 'crawledAt',
    width: 72,
    render: (val) => {
      const d = dayjs(val);
      if (!d.isValid()) return <Text type="secondary" style={{ fontSize: 12 }}>-</Text>;
      const dow = DOW[d.day()];
      const isWeekend = d.day() === 0 || d.day() === 6;
      return (
        <div style={{ whiteSpace: 'nowrap', textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 13 }}>
            {d.format('MM/DD')}
            <span style={{ color: isWeekend ? '#f5222d' : '#595959', fontSize: 11, marginLeft: 2 }}>
              ({dow})
            </span>
          </div>
        </div>
      );
    },
  };

  const articleColumn: ColumnsType<SocialArticle>[0] = {
    title: '기사',
    dataIndex: 'title',
    key: 'title',
    render: (text, record) => {
      const d = dayjs(record.crawledAt);
      const dow = d.isValid() ? DOW[d.day()] : '';
      const isWeekend = d.day() === 0 || d.day() === 6;

      return (
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          {record.imageUrl && !isMobile && (
            <img
              src={record.imageUrl}
              alt=""
              style={{ width: 72, height: 50, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
            />
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
            {/* 모바일: 날짜를 제목 위에 표시 */}
            {isMobile && d.isValid() && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                <span style={{ fontWeight: 600, color: isWeekend ? '#f5222d' : '#595959' }}>
                  {d.format('MM/DD')}({dow})
                </span>
              </Text>
            )}
            <a
              href={record.link}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: isMobile ? 13 : 14, wordBreak: 'keep-all' }}
            >
              {text}
            </a>
            {record.summary && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                {record.summary.slice(0, isMobile ? 60 : 100)}
                {record.summary.length > (isMobile ? 60 : 100) ? '...' : ''}
              </Text>
            )}
            <Space size={4} wrap>
              <Tag color="cyan" style={{ fontSize: 10, padding: '0 4px' }}>
                {SOURCE_LABEL[record.source] ?? record.source}
              </Tag>
              {record.beat?.map((b) => (
                <Tag key={b} color={BEAT_COLOR[b] ?? 'default'} style={{ fontSize: 10, padding: '0 4px' }}>
                  {b}
                </Tag>
              ))}
              {record.matchedKeywords?.map((kw) => (
                <Tag
                  key={kw}
                  style={{ fontSize: 10, padding: '0 4px', cursor: 'pointer' }}
                  onClick={() => { setMatchedKeyword(kw); setPage(1); }}
                >
                  {kw}
                </Tag>
              ))}
              {record.author && !isMobile && (
                <Text type="secondary" style={{ fontSize: 11 }}>✍️ {record.author}</Text>
              )}
            </Space>
          </div>
        </div>
      );
    },
  };

  const columns: ColumnsType<SocialArticle> = isMobile
    ? [articleColumn]
    : [desktopDateColumn, articleColumn];

  return (
    <div>
      {/* 담당 구역 안내 */}
      <Collapse
        size="small"
        style={{ marginBottom: 12 }}
        defaultActiveKey={['coverage']}
        items={[
          {
            key: 'coverage',
            label: '담당 구역 보기',
            children: (
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', flexWrap: 'wrap', gap: 12 }}>
                {BEAT_COVERAGE.map((group) => (
                  <div key={group.beat} style={{ minWidth: isMobile ? '100%' : 200 }}>
                    <Tag color={group.color} style={{ fontWeight: 600, marginBottom: 6 }}>
                      {group.beat}
                    </Tag>
                    {group.items.map((item) => (
                      <div key={item.label} style={{ marginBottom: 4 }}>
                        <Text type="secondary" style={{ fontSize: 11, marginRight: 4 }}>
                          {item.label}
                        </Text>
                        <Space size={2} wrap>
                          {item.tags.map((tag) => (
                            <Tag
                              key={tag}
                              style={{ fontSize: 11, cursor: 'pointer', marginBottom: 2 }}
                              onClick={() => { setMatchedKeyword(tag); setPage(1); }}
                            >
                              {tag}
                            </Tag>
                          ))}
                        </Space>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ),
          },
        ]}
      />

      {/* 필터 — 모바일: 2열 그리드 */}
      {isMobile ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
          <Input.Search
            placeholder="제목/본문 검색"
            allowClear
            style={{ gridColumn: '1 / -1' }}
            onSearch={(val) => { setKeyword(val); setPage(1); }}
          />
          <Select
            options={BEAT_OPTIONS}
            value={beat}
            onChange={(val) => { setBeat(val); setPage(1); }}
            style={{ width: '100%' }}
            placeholder="라인 선택"
          />
          <DatePicker
            value={selectedDate}
            onChange={(date) => { setSelectedDate(date); setPage(1); }}
            placeholder="날짜 선택"
            style={{ width: '100%' }}
            allowClear
          />
          <Input.Search
            placeholder="기관 검색"
            allowClear
            value={matchedKeyword}
            style={{ gridColumn: '1 / -1' }}
            onChange={(e) => { if (!e.target.value) { setMatchedKeyword(''); setPage(1); } }}
            onSearch={(val) => { setMatchedKeyword(val); setPage(1); }}
          />
        </div>
      ) : (
        <Space style={{ marginBottom: 12, flexWrap: 'wrap' }} size={8}>
          <Input.Search
            placeholder="제목/본문 검색"
            allowClear
            style={{ width: 220 }}
            onSearch={(val) => { setKeyword(val); setPage(1); }}
          />
          <Select
            options={BEAT_OPTIONS}
            value={beat}
            onChange={(val) => { setBeat(val); setPage(1); }}
            style={{ width: 140 }}
          />
          <Input.Search
            placeholder="기관 검색 (예: 북부지검)"
            allowClear
            value={matchedKeyword}
            style={{ width: 200 }}
            onChange={(e) => { if (!e.target.value) { setMatchedKeyword(''); setPage(1); } }}
            onSearch={(val) => { setMatchedKeyword(val); setPage(1); }}
          />
          <DatePicker
            value={selectedDate}
            onChange={(date) => { setSelectedDate(date); setPage(1); }}
            placeholder="날짜 선택"
            style={{ width: 140 }}
            allowClear
          />
        </Space>
      )}

      <Table
        columns={columns}
        dataSource={data?.contents ?? []}
        rowKey="_id"
        loading={isLoading}
        pagination={{
          current: page,
          pageSize: limit,
          total: data?.total ?? 0,
          onChange: (p) => setPage(p),
          showTotal: (total) => `총 ${total}개`,
          size: isMobile ? 'small' : 'default',
        }}
        size="small"
      />
    </div>
  );
};

export default SocialNews;
